import { Stack, Card, Title, Text, Group, Button } from '@repletoryreach/sdk-ui';
import { ThemeToggle } from '@repletoryreach/sdk-ui';
import { SegmentedControl, Select } from '@repletoryreach/sdk-ui/inputs';
import { useAppStore, type Density, type ReducedMotion } from '@repletoryreach/sdk-utils/store';
import { useT } from '../../../i18n/I18nProvider';
import { LOCALES, type Locale } from '../../../i18n/catalog';
import { localeCoverage } from '../../../i18n/coverage';
import { useLocalePreference } from '../../../lib/useLocalePreference';
import { usePersistedState } from '../../../lib/usePersistedState';
import type { ReactNode } from 'react';

/** Sample rows for the density preview. People's names, never translated. */
const SAMPLE_NAMES = ['Thandi Mokoena', 'Sipho Dlamini', 'Ayesha Patel']; // i18n-ignore: sample data, not UI copy
import './appearance.css';

export default function SettingsAppearancePage() {
  const t = useT();
  const density = useAppStore((s) => s.density);
  const setDensity = useAppStore((s) => s.setDensity);
  const reducedMotion = useAppStore((s) => s.reducedMotion);
  const setReducedMotion = useAppStore((s) => s.setReducedMotion);

  // §4 item 1: this used to write client state only, so the choice did not
  // survive a new device and disagreed with the Profile page's picker.
  const { locale, setLocale } = useLocalePreference();

  // dashboard.md §4 item 12: dismissing the setup checklist was a one-way door
  // with no way back. This is the way back.
  const [checklistDismissed, setChecklistDismissed] = usePersistedState('tenant.setupChecklist.dismissed', false);

  return (
    <Stack gap="md">
      <SectionCard title={t('settings.appearance.theme')} description={t('settings.appearance.themeHint')}>
        {/* §4 item 8: dropped in bare, so a screen reader got only whatever
            the component labels itself. */}
        <div role="group" aria-label={t('settings.appearance.theme')}>
          <ThemeToggle />
        </div>
      </SectionCard>

      <SectionCard title={t('common.language')} description={t('settings.appearance.languageHint')}>
        <Select
          aria-label={t('common.language')}
          value={locale}
          onChange={(e) => setLocale(e.currentTarget.value as Locale)}
          // Partly-translated languages are labelled as such, the same way the
          // sidebar picker does it, rather than looking complete.
          data={LOCALES.map((l) => {
            const pct = localeCoverage(l.code);
            return {
              value: l.code,
              label: pct === 100 ? l.label : t('shell.locale.partial', { label: l.label, percent: pct })
            };
          })}
        />
      </SectionCard>

      <SectionCard title={t('settings.appearance.density')} description={t('settings.appearance.densityHint')}>
        <Stack gap="sm">
          <SegmentedControl
            size="xs"
            aria-label={t('settings.appearance.density')}
            value={density}
            onChange={(v) => setDensity(v as Density)}
            data={[
              { value: 'comfortable', label: t('settings.appearance.comfortable') },
              { value: 'cozy', label: t('settings.appearance.cozy') },
              { value: 'compact', label: t('settings.appearance.compact') }
            ]}
          />

          {/* §4 item 2: the copy named the options and never said what changes.
              Three sample rows say it without a word. */}
          <div className={`rr-appearance__preview rr-appearance__preview--${density}`} aria-hidden>
            {SAMPLE_NAMES.map((name) => (
              <div key={name} className="rr-appearance__row">
                <span className="rr-appearance__avatar" />
                <span className="rr-appearance__name">{name}</span>
                <span className="rr-appearance__meta">+27 82 111 0000</span>
              </div>
            ))}
          </div>
          <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.appearance.densityPreview')}</Text>
        </Stack>
      </SectionCard>

      <SectionCard title={t('settings.appearance.motion')} description={t('settings.appearance.motionHint')}>
        {/* §4 item 3: every sdk-ui animation honours `reducedMotion` and the
            console never exposed the control.

            A switch would misrepresent it: the setting is three-way, and
            `auto` (follow the operating system) is the one most people want,
            so collapsing it to on/off would silently override an OS-level
            accessibility preference. */}
        <SegmentedControl
          size="xs"
          aria-label={t('settings.appearance.reduceMotion')}
          value={reducedMotion}
          onChange={(v) => setReducedMotion(v as ReducedMotion)}
          data={[
            { value: 'auto', label: t('settings.appearance.motion.auto') },
            { value: 'always', label: t('settings.appearance.motion.always') },
            { value: 'never', label: t('settings.appearance.motion.never') }
          ]}
        />
      </SectionCard>

      <SectionCard title={t('settings.appearance.guidance')} description={t('settings.appearance.guidanceHint')}>
        <Group justify="space-between" align="center" wrap="wrap" gap="sm">
          <Text role="bodySmall">{t('setup.restore')}</Text>
          {checklistDismissed ? (
            <Button size="xs" variant="default" onClick={() => setChecklistDismissed(false)}>
              {t('setup.restore')}
            </Button>
          ) : (
            <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.appearance.checklistShowing')}</Text>
          )}
        </Group>
      </SectionCard>
    </Stack>
  );
}

function SectionCard({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  return (
    <Card withBorder>
      <Stack gap="sm">
        <Stack gap={2}>
          <Title order={5}>{title}</Title>
          <Text role="caption" c="var(--rr-color-text-muted)">{description}</Text>
        </Stack>
        {children}
      </Stack>
    </Card>
  );
}
